import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowClockwise,
  Broadcast,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';

import { API_URL } from '../config/api';

function StatusIcon({ status }) {
  switch (status) {
    case 'verified': return <CheckCircle size={18} weight="fill" className="text-emerald-500" />;
    case 'rejected': return <XCircle size={18} weight="fill" className="text-red-500" />;
    case 'expired': return <Clock size={18} weight="fill" className="text-slate-400" />;
    default: return <ArrowClockwise size={18} className="text-amber-500 animate-spin" />;
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'verified': return 'Approved';
    case 'rejected': return 'Rejected';
    case 'expired': return 'Expired';
    default: return 'Pending';
  }
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function StatsRow({ connected, stats }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
      <Card className="dashboard-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Stream</p>
            <p className="text-sm font-bold" data-testid="live-connection-status">{connected ? 'Connected' : 'Reconnecting...'}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="dashboard-card">
        <CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-slate-900" data-testid="live-stat-total">{stats.total}</p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total USSD</p>
        </CardContent>
      </Card>
      <Card className="dashboard-card">
        <CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600" data-testid="live-stat-approved">{stats.approved}</p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Approved</p>
        </CardContent>
      </Card>
      <Card className="dashboard-card">
        <CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-red-600" data-testid="live-stat-rejected">{stats.rejected}</p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rejected</p>
        </CardContent>
      </Card>
      <Card className="dashboard-card">
        <CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600" data-testid="live-stat-approval-rate">{stats.approval_rate}%</p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Approval Rate</p>
        </CardContent>
      </Card>
    </div>
  );
}

function getStatusBorderClass(status) {
  if (status === 'verified') return 'border-emerald-200 bg-emerald-50/50';
  if (status === 'rejected') return 'border-red-200 bg-red-50/50';
  return 'border-slate-200 bg-slate-50/50';
}

function EventFeedItem({ evt, isLatest, connected }) {
  const animClass = isLatest && connected ? 'animate-in fade-in slide-in-from-top-2 duration-300' : '';
  const borderClass = getStatusBorderClass(evt.status);

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${animClass} ${borderClass}`}
      data-testid={`live-event-${evt.session_id}`}
    >
      <StatusIcon status={evt.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">
            {getStatusLabel(evt.status)}
          </span>
          <Badge variant="outline" className="text-[10px]">{evt.transaction_reference}</Badge>
          {evt.provider && (
            <Badge variant="outline" className="text-[10px] bg-slate-50">{evt.provider}</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
          <span className="font-mono">{evt.phone_number}</span>
          {evt.transaction_details && <span className="truncate">{evt.transaction_details}</span>}
          {evt.cost > 0 && <span className="font-mono">R{evt.cost.toFixed(2)}</span>}
        </div>
      </div>
      <div className="text-xs text-slate-400 whitespace-nowrap">
        {formatTime(evt.timestamp)}
      </div>
    </div>
  );
}

function FeedContent({ initialLoaded, events, connected }) {
  if (!initialLoaded) {
    return <div className="text-center py-8 text-slate-400">Loading...</div>;
  }
  if (events.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Broadcast size={40} className="mx-auto mb-3 opacity-40" />
        <p className="text-sm">No USSD sessions yet. Events will appear here in real-time.</p>
      </div>
    );
  }
  return events.map((evt, idx) => (
    <EventFeedItem
      key={`${evt.session_id}-${idx}`}
      evt={evt}
      isLatest={idx === 0}
      connected={connected}
    />
  ));
}


export function LiveUSSDMonitor() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, rejected: 0, pending: 0, expired: 0, approval_rate: 0 });
  const [connected, setConnected] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const eventSourceRef = useRef(null);
  const feedRef = useRef(null);

  const loadRecentSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client/ussd-sessions/recent`, { withCredentials: true });
      const data = res.data;
      setStats(data.stats);
      const sessionEvents = (data.sessions || []).map(s => ({
        type: 'ussd_response',
        session_id: s.session_id,
        status: s.status,
        action: s.status === 'verified' ? 'approve' : s.status === 'rejected' ? 'reject' : s.status,
        transaction_reference: s.transaction_reference,
        phone_number: s.phone_number,
        transaction_details: s.transaction_details,
        cost: s.cost,
        provider: s.ussd_provider || '',
        timestamp: s.responded_at || s.created_at,
      }));
      setEvents(sessionEvents);
      setInitialLoaded(true);
    } catch {
      setInitialLoaded(true);
    }
  }, []);

  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const es = new EventSource(`${API_URL}/api/client/ussd-live`, { withCredentials: true });

    es.onopen = () => setConnected(true);

    es.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data);
        setEvents(prev => [event, ...prev].slice(0, 50));
        setStats(prev => {
          const newApproved = prev.approved + (event.status === 'verified' ? 1 : 0);
          const newRejected = prev.rejected + (event.status === 'rejected' ? 1 : 0);
          const totalDecisions = newApproved + newRejected;
          return {
            ...prev,
            total: prev.total + 1,
            approved: newApproved,
            rejected: newRejected,
            approval_rate: totalDecisions > 0 ? Math.round((newApproved / totalDecisions) * 1000) / 10 : 0,
          };
        });
      } catch {
        // Ignore SSE heartbeat parse errors
      }
    };

    es.onerror = () => {
      setConnected(false);
      es.close();
      setTimeout(connectSSE, 5000);
    };

    eventSourceRef.current = es;
  }, []);

  useEffect(() => {
    loadRecentSessions();
    connectSSE();
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [loadRecentSessions, connectSSE]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = 0;
    }
  }, [events]);

  return (
    <div className="space-y-6">
      <StatsRow connected={connected} stats={stats} />

      <Card className="dashboard-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Broadcast size={20} weight="duotone" className="text-blue-500" /> Approval Rate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Progress
                value={stats.approval_rate}
                className="h-4 [&>div]:bg-emerald-500"
                data-testid="live-approval-progress"
              />
            </div>
            <span className="text-lg font-bold text-slate-700 w-16 text-right">{stats.approval_rate}%</span>
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
            <span>{stats.approved} approved</span>
            <span>{stats.rejected} rejected</span>
            <span>{stats.pending} pending</span>
            <span>{stats.expired} expired</span>
          </div>
        </CardContent>
      </Card>

      <Card className="dashboard-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              Live USSD Activity Feed
              {connected && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadRecentSessions} data-testid="refresh-live-feed">
              <ArrowClockwise size={16} className="mr-1" /> Refresh
            </Button>
          </div>
          <CardDescription>Real-time approve/reject responses from USSD sessions</CardDescription>
        </CardHeader>
        <CardContent>
          <div ref={feedRef} className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="live-event-feed">
            <FeedContent
              initialLoaded={initialLoaded}
              events={events}
              connected={connected}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
